import { roundMoney } from "@/domain/money/round-money";
import { buildCartLineValidityCapture, cartLinesToCaptures } from "@/domain/cart/cart-line-capture";
import { resolveCartValidity } from "@/domain/cart/cart-validity.resolver";
import type {
  CartValidityViewModel,
  MenuCatalogItem,
  MenuCatalogSnapshot,
} from "@/domain/cart/cart-validity.types";
import type {
  AcceptancePolicyOutcome,
  BlockingPolicyViewModel,
  OrderValidationIssueWire,
} from "@/domain/order/acceptance-policy.types";
import type { CartLine, CartLineValidityCapture } from "@/shared/cart/model.types";

/** Same tolerance cart validity uses so rounding is not reported as a price change. */
const MONEY_EPSILON = 0.005;

const PRICE_NOTICE_STORAGE_KEY = "rasoix.checkout.price-notice";

const BLOCKING_LINE_CODES = new Set([
  "ITEM_REMOVED",
  "ITEM_UNAVAILABLE",
  "MODIFIER_GROUP_REMOVED",
  "MODIFIER_REMOVED",
  "MODIFIER_UNAVAILABLE",
  "COMBO_CHILD_REMOVED",
]);

export type CheckoutPriceChangeKind = "item" | "modifier";

/** One customer-visible price difference for a cart line or one of its options. */
export type CheckoutPriceChange = {
  clientItemId: string;
  itemName: string;
  kind: CheckoutPriceChangeKind;
  modifierId?: string;
  /** Item name, or the option name when only that option's price moved. */
  label: string;
  previousPrice: number;
  currentPrice: number;
  /** Units this difference applies to (line quantity, or line × option quantity). */
  pricedQuantity: number;
};

export type LinePricePresentation = {
  clientItemId: string;
  previousUnitPrice: number;
  currentUnitPrice: number;
  showUnitTransition: boolean;
  /** Added to the captured line total so the row shows the current price. */
  lineTotalAdjustment: number;
  modifierLines: Array<{ label: string; previousPrice: number; currentPrice: number }>;
};

export type CheckoutPriceChangeCopy = {
  title: string;
  message: string;
  details: string[];
};

function pricesDiffer(previous: number, current: number): boolean {
  return Math.abs(roundMoney(previous) - roundMoney(current)) >= MONEY_EPSILON;
}

function pricesMatch(previous: number, current: number): boolean {
  return !pricesDiffer(previous, current);
}

function catalogDelta(item: MenuCatalogItem, modifierId: string): number | null {
  for (const group of item.modifier_groups ?? []) {
    const match = group.items?.find((entry) => String(entry.modifier_group_item_id) === modifierId);
    if (match) return roundMoney(match.delta_price);
  }
  return null;
}

function withUpdatedModifierDelta(
  modifiers: CartLine["selected_modifiers"],
  modifierId: string,
  nextDelta: number
): { modifiers: CartLine["selected_modifiers"]; changed: boolean } {
  let changed = false;
  const next = modifiers.map((group) => ({
    ...group,
    items: group.items.map((mod) => {
      if (String(mod.modifier_group_item_id) !== modifierId) return mod;
      if (pricesMatch(mod.delta_price, nextDelta)) return mod;
      changed = true;
      return { ...mod, delta_price: nextDelta };
    }),
  }));
  return { modifiers: changed ? next : modifiers, changed };
}

function capturesMatch(current: CartLineValidityCapture | undefined, next: CartLineValidityCapture): boolean {
  if (!current) return false;
  return (
    pricesMatch(current.capturedUnitPrice, next.capturedUnitPrice) &&
    current.capturedMenuVersion === next.capturedMenuVersion &&
    current.capturedCatalogFingerprint === next.capturedCatalogFingerprint &&
    current.capturedModifierIds.join("|") === next.capturedModifierIds.join("|")
  );
}

function moneyKey(value: number): string {
  return roundMoney(value).toFixed(2);
}

function modifierLabel(
  catalogItems: readonly MenuCatalogItem[] | undefined,
  menuItemId: string | undefined,
  modifierId: string
): string {
  if (!catalogItems || !menuItemId) return "Option";
  const item = catalogItems.find((entry) => String(entry.menu_item_id) === String(menuItemId));
  for (const group of item?.modifier_groups ?? []) {
    const match = group.items?.find((entry) => String(entry.modifier_group_item_id) === modifierId);
    const name = match?.child_menu_item_name?.trim();
    if (name) return name;
  }
  return "Option";
}

function modifierPricedQuantity(line: CartLine, modifierId: string): number {
  const lineQty = Math.max(0, line.quantity || 0);
  for (const group of line.selected_modifiers ?? []) {
    const match = group.items?.find((entry) => String(entry.modifier_group_item_id) === modifierId);
    if (!match) continue;
    const optionQty = match.quantity == null || match.quantity <= 0 ? 1 : match.quantity;
    return lineQty * optionQty;
  }
  return lineQty;
}

/**
 * Compares the price the customer added with the current catalog price already
 * recorded on cart validity. Unavailable lines stay on the unavailable flow.
 */
export function collectCheckoutPriceChanges(
  validity: CartValidityViewModel,
  lines: readonly CartLine[],
  catalogItems?: readonly MenuCatalogItem[]
): CheckoutPriceChange[] {
  const lineById = new Map(lines.map((line) => [line.client_item_id, line]));
  const blocked = new Set<string>();
  for (const issue of validity.issues) {
    if (!issue.client_item_id) continue;
    if (issue.severity === "blocking" || BLOCKING_LINE_CODES.has(issue.code)) {
      blocked.add(issue.client_item_id);
    }
  }

  const changes: CheckoutPriceChange[] = [];
  for (const issue of validity.issues) {
    if (issue.code !== "STALE_PRICE" || !issue.client_item_id) continue;
    if (blocked.has(issue.client_item_id)) continue;
    const previous = issue.context?.expectedPrice;
    const current = issue.context?.currentPrice;
    if (previous == null || current == null || !pricesDiffer(previous, current)) continue;

    const line = lineById.get(issue.client_item_id);
    const itemName = line?.item_name?.trim() || "Item";
    const modifierId = issue.modifier_group_item_id?.trim();
    if (modifierId) {
      changes.push({
        clientItemId: issue.client_item_id,
        itemName,
        kind: "modifier",
        modifierId,
        label: modifierLabel(catalogItems, issue.menu_item_id, modifierId),
        previousPrice: roundMoney(previous),
        currentPrice: roundMoney(current),
        pricedQuantity: line ? modifierPricedQuantity(line, modifierId) : 1,
      });
      continue;
    }

    changes.push({
      clientItemId: issue.client_item_id,
      itemName,
      kind: "item",
      label: itemName,
      previousPrice: roundMoney(previous),
      currentPrice: roundMoney(current),
      pricedQuantity: Math.max(1, line?.quantity || 1),
    });
  }

  return changes;
}

/** Stable identity of a pricing refresh. Same prices do not announce again. */
export function checkoutPriceChangeSignature(changes: readonly CheckoutPriceChange[]): string {
  if (changes.length === 0) return "";
  return changes
    .map(
      (change) =>
        `${change.clientItemId}|${change.kind}|${change.modifierId ?? ""}|${moneyKey(change.previousPrice)}|${moneyKey(change.currentPrice)}`
    )
    .sort()
    .join(";");
}

export function readAnnouncedPriceSignature(): string | null {
  try {
    return sessionStorage.getItem(PRICE_NOTICE_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function rememberAnnouncedPriceSignature(signature: string): void {
  try {
    sessionStorage.setItem(PRICE_NOTICE_STORAGE_KEY, signature);
  } catch {
    /* private mode or disabled storage — the in-memory notice still shows once */
  }
}

/**
 * True when this exact old→new set has not been announced yet.
 * Callers remember the signature after showing it.
 */
export function shouldAnnouncePriceChange(signature: string, remembered: string | null): boolean {
  return signature.length > 0 && signature !== remembered;
}

/**
 * True when the generic validity banner would only repeat this price notice.
 * A catalog fingerprint change often accompanies the price move; that is the
 * same event. Blocking issues (including unavailable items) keep their banner.
 */
export function isExclusivePriceUpdate(validity: CartValidityViewModel): boolean {
  if (validity.issues.some((issue) => issue.severity === "blocking")) return false;
  return validity.issues.some((issue) => issue.code === "STALE_PRICE");
}

function acceptLinePrice(
  line: CartLine,
  catalog: MenuCatalogSnapshot,
  changes: readonly CheckoutPriceChange[]
): CartLine {
  const catalogItem = catalog.items.find((item) => String(item.menu_item_id) === String(line.menu_item_id));
  if (!catalogItem || catalogItem.is_deleted === true || catalogItem.is_available === false) return line;

  const mine = changes.filter((change) => change.clientItemId === line.client_item_id);
  let modifiers = line.selected_modifiers;
  let modifiersChanged = false;
  for (const change of mine) {
    if (change.kind !== "modifier" || !change.modifierId) continue;
    const nextDelta = catalogDelta(catalogItem, change.modifierId);
    if (nextDelta == null) continue;
    const updated = withUpdatedModifierDelta(modifiers, change.modifierId, nextDelta);
    if (!updated.changed) continue;
    modifiers = updated.modifiers;
    modifiersChanged = true;
  }

  const nextPrice = roundMoney(catalogItem.item_price);
  const capture = buildCartLineValidityCapture({
    item_price: nextPrice,
    menu_version: catalogItem.menu_version,
    catalog_fingerprint: catalog.fingerprint,
    selected_modifiers: modifiersChanged ? modifiers : line.selected_modifiers,
  });
  if (
    pricesMatch(line.item_price, nextPrice) &&
    !modifiersChanged &&
    capturesMatch(line.validityCapture, capture)
  ) {
    return line;
  }

  return {
    ...line,
    item_price: nextPrice,
    selected_modifiers: modifiersChanged ? modifiers : line.selected_modifiers,
    validityCapture: capture,
  };
}

/**
 * Writes the current catalog price onto the cart lines the customer was just
 * shown. This clears STALE_PRICE through the existing validity capture.
 * Unavailable lines are left untouched. Returns null when nothing changed,
 * so callers do not write the cart again.
 */
export function synchronizeCartLinesToCatalog(
  lines: readonly CartLine[],
  catalog: MenuCatalogSnapshot,
  changes: readonly CheckoutPriceChange[]
): CartLine[] | null {
  if (!catalog.loaded || changes.length === 0) return null;

  const changeIds = new Set(changes.map((change) => change.clientItemId));
  let mutated = false;
  let next = lines.map((line) => {
    if (!changeIds.has(line.client_item_id)) return line;
    const updated = acceptLinePrice(line, catalog, changes);
    if (updated !== line) mutated = true;
    return updated;
  });

  const validity = resolveCartValidity({
    cartLines: [...next],
    catalog,
    captures: cartLinesToCaptures(next),
  });
  const blocking = validity.issues.some((issue) => issue.severity === "blocking");
  const stillStalePrice = validity.issues.some((issue) => issue.code === "STALE_PRICE");
  const fingerprintOnly =
    !blocking &&
    !stillStalePrice &&
    validity.issues.some((issue) => issue.code === "MENU_VERSION_CHANGED" && !issue.client_item_id);

  if (fingerprintOnly) {
    next = next.map((line) => {
      if (!line.validityCapture) return line;
      if (line.validityCapture.capturedCatalogFingerprint === catalog.fingerprint) return line;
      mutated = true;
      return {
        ...line,
        validityCapture: {
          ...line.validityCapture,
          capturedCatalogFingerprint: catalog.fingerprint,
        },
      };
    });
  }

  return mutated ? next : null;
}

/**
 * Keeps the price notice for this checkout visit after the cart capture
 * catches up. A later, different price set replaces it. An already-seen
 * set does not start a new notice.
 */
export function retainCheckoutPriceNotice(
  visitNotice: readonly CheckoutPriceChange[] | null,
  liveChanges: readonly CheckoutPriceChange[],
  remembered: string | null
): readonly CheckoutPriceChange[] | null {
  const signature = checkoutPriceChangeSignature(liveChanges);
  if (signature && shouldAnnouncePriceChange(signature, remembered)) return liveChanges;
  return visitNotice;
}

export function linePricePresentations(
  changes: readonly CheckoutPriceChange[]
): Map<string, LinePricePresentation> {
  const byLine = new Map<string, LinePricePresentation>();
  for (const change of changes) {
    const existing = byLine.get(change.clientItemId) ?? {
      clientItemId: change.clientItemId,
      previousUnitPrice: change.previousPrice,
      currentUnitPrice: change.currentPrice,
      showUnitTransition: false,
      lineTotalAdjustment: 0,
      modifierLines: [],
    };
    const adjustment = roundMoney((change.currentPrice - change.previousPrice) * change.pricedQuantity);
    existing.lineTotalAdjustment = roundMoney(existing.lineTotalAdjustment + adjustment);
    if (change.kind === "item") {
      existing.previousUnitPrice = change.previousPrice;
      existing.currentUnitPrice = change.currentPrice;
      existing.showUnitTransition = true;
    } else {
      existing.modifierLines.push({
        label: change.label,
        previousPrice: change.previousPrice,
        currentPrice: change.currentPrice,
      });
    }
    byLine.set(change.clientItemId, existing);
  }
  return byLine;
}

export function checkoutPriceChangeCopy(
  changes: readonly CheckoutPriceChange[],
  format: (amount: number) => string
): CheckoutPriceChangeCopy {
  const details = changes.map((change) => {
    const name =
      change.kind === "modifier" ? `${change.itemName} — ${change.label}` : change.itemName;
    return `${name}: ${format(change.previousPrice)} → ${format(change.currentPrice)}`;
  });

  if (changes.length === 1) {
    const change = changes[0];
    const review = " Please review the updated price before continuing.";
    const message =
      change.kind === "modifier"
        ? `The price of ${change.label} on ${change.itemName} has changed from ${format(change.previousPrice)} to ${format(change.currentPrice)}.${review}`
        : `The price of ${change.itemName} has changed from ${format(change.previousPrice)} to ${format(change.currentPrice)}.${review}`;
    return { title: "Price updated", message, details };
  }

  return {
    title: "Some prices have changed",
    message: "One or more item prices were updated. Please review your order before continuing.",
    details,
  };
}

/** True only for a price drift recorded in this checkout session that is not yet confirmed. */
export function isPriceChangeAcknowledgementRequired(
  signature: string | null | undefined,
  acknowledgedSignature: string | null | undefined
): boolean {
  return typeof signature === "string" && signature.length > 0 && signature !== acknowledgedSignature;
}

/** One checkbox for the whole group. A single item uses the singular wording. */
const PRICE_UPDATED_TITLE = "Price updated";
const PRICE_UPDATED_ONE =
  "The price of one or more items has changed. Please review the updated price before continuing.";
const PRICE_UPDATED_MANY =
  "The prices of some items in your order have changed. Please review the updated prices before continuing.";

/** Sheet content for a price-only conflict. Uses the existing review-sheet shape. */
export function priceUpdatedPolicyViewModel(
  changes: readonly CheckoutPriceChange[],
  format: (amount: number) => string,
  resultCode = 4098
): BlockingPolicyViewModel {
  const itemCount = new Set(changes.map((change) => change.clientItemId)).size;
  return {
    reason: "ORDER_VALIDATION_FAILED",
    resultCode,
    title: PRICE_UPDATED_TITLE,
    message: itemCount > 1 ? PRICE_UPDATED_MANY : PRICE_UPDATED_ONE,
    actions: [{ id: "dismiss", label: "Review order" }],
    metaLines: priceUpdatedMetaLines(changes, format),
  };
}

function priceUpdatedMetaLines(
  changes: readonly CheckoutPriceChange[],
  format: (amount: number) => string
): string[] {
  const groups = new Map<string, CheckoutPriceChange[]>();
  for (const change of changes) {
    const list = groups.get(change.clientItemId) ?? [];
    list.push(change);
    groups.set(change.clientItemId, list);
  }

  const lines: string[] = [];
  for (const group of groups.values()) {
    const itemChange = group.find((change) => change.kind === "item");
    const rows = [`• ${group[0].itemName}`];
    if (itemChange) {
      rows.push(`${format(itemChange.previousPrice)} → ${format(itemChange.currentPrice)}`);
    }
    for (const modifier of group) {
      if (modifier.kind !== "modifier") continue;
      rows.push(modifier.label);
      rows.push(`${format(modifier.previousPrice)} → ${format(modifier.currentPrice)}`);
    }
    lines.push(rows.join("\n"));
  }
  return lines;
}

/** True when a 4098 body is only PRICE_CHANGED and must not open the generic sheet. */
export function isExclusiveServerPriceChange(outcome: AcceptancePolicyOutcome): boolean {
  if (outcome.kind !== "blocking_policy" || outcome.reason !== "ORDER_VALIDATION_FAILED") return false;
  const issues = outcome.validationIssues;
  return Boolean(issues && issues.length > 0 && issues.every((issue) => issue.kind === "PRICE_CHANGED"));
}

/**
 * Turns server PRICE_CHANGED issues into the existing notice model, then
 * writes the current catalog price onto those lines. The response identifies
 * the drift. The catalog snapshot supplies the cart price.
 */
export function acceptServerPriceDrift(
  lines: readonly CartLine[],
  catalog: MenuCatalogSnapshot | null,
  issues: readonly OrderValidationIssueWire[]
): { changes: CheckoutPriceChange[]; lines: CartLine[] | null; signature: string } {
  const changes = checkoutPriceChangesFromServerIssues(issues, lines);
  const signature = checkoutPriceChangeSignature(changes);
  if (!catalog?.loaded || changes.length === 0) {
    return { changes, lines: null, signature };
  }
  return {
    changes,
    lines: synchronizeCartLinesToCatalog(lines, catalog, changes),
    signature,
  };
}

function checkoutPriceChangesFromServerIssues(
  issues: readonly OrderValidationIssueWire[],
  lines: readonly CartLine[]
): CheckoutPriceChange[] {
  const changes: CheckoutPriceChange[] = [];
  for (const issue of issues) {
    if (issue.kind !== "PRICE_CHANGED") continue;
    if (issue.reviewed_price == null || issue.current_price == null) continue;
    if (!pricesDiffer(issue.reviewed_price, issue.current_price)) continue;

    const line =
      lines.find((entry) => entry.client_item_id === issue.client_item_id) ??
      lines.find((entry) => entry.menu_item_id === issue.menu_item_id);
    if (!line) continue;

    const itemName = issue.item_name?.trim() || line.item_name?.trim() || "Item";
    if (issue.modifier_item_id) {
      const modifierId = cartModifierId(line, issue.modifier_item_id);
      changes.push({
        clientItemId: line.client_item_id,
        itemName,
        kind: "modifier",
        modifierId,
        label: issue.modifier_item_name?.trim() || "Option",
        previousPrice: roundMoney(issue.reviewed_price),
        currentPrice: roundMoney(issue.current_price),
        pricedQuantity: modifierPricedQuantity(line, modifierId),
      });
      continue;
    }

    changes.push({
      clientItemId: line.client_item_id,
      itemName,
      kind: "item",
      label: itemName,
      previousPrice: roundMoney(issue.reviewed_price),
      currentPrice: roundMoney(issue.current_price),
      pricedQuantity: Math.max(1, line.quantity || 1),
    });
  }
  return changes;
}

function cartModifierId(line: CartLine, modifierItemId: string): string {
  for (const group of line.selected_modifiers ?? []) {
    const match = group.items?.find(
      (entry) =>
        entry.child_menu_item_id === modifierItemId || entry.modifier_group_item_id === modifierItemId
    );
    if (match?.modifier_group_item_id) return match.modifier_group_item_id;
  }
  return modifierItemId;
}

export function priceChangeAcknowledgementLabel(itemCount: number): string {
  return itemCount > 1
    ? "I understand that the item prices above were updated."
    : "I understand that the price of this item was updated.";
}
